import { DynamicIcon, type IconName } from 'lucide-react/dynamic'
import { Tag } from 'lucide-react'
import { CATEGORY_ICON_OPTIONS } from '@/lib/categoryIconOptions'
import { ACCOUNT_ICON_OPTIONS } from '@/lib/accountIconOptions'
import { BILL_ICON_OPTIONS } from '@/lib/billIconOptions'
import { SUBSCRIPTION_ICON_OPTIONS } from '@/lib/subscriptionIconOptions'
import { GOAL_ICON_OPTIONS } from '@/lib/goalIconOptions'
import { ALL_CATALOG_ICON_NAMES } from '@/lib/iconCatalog'

// Names we render as a real glyph: every picker's suggestions and the shared catalog, plus the icons the backend's
// DefaultCategorySeeder assigns to every household's default categories that the picker
// doesn't offer, plus a few common pre-picker free-typed names. Anything else — an emoji,
// a typo — falls back to the raw string.
//
// Deliberately NOT derived from lucide-react's `iconNames` export: that is
// `Object.keys(dynamicIconImports)` computed at module-load inside lucide's barrel, and
// has come back empty in some production bundles — which made every icon in the app
// render as its raw name ("home Housing").
const SEEDED_ICON_NAMES = ['bag', 'smile', 'repeat', 'circle', 'laptop', 'building']

const KNOWN_ICON_NAMES = new Set<string>([
  ...CATEGORY_ICON_OPTIONS.map((o) => o.name),
  ...ACCOUNT_ICON_OPTIONS.map((o) => o.name),
  ...BILL_ICON_OPTIONS.map((o) => o.name),
  ...SUBSCRIPTION_ICON_OPTIONS.map((o) => o.name),
  ...GOAL_ICON_OPTIONS.map((o) => o.name),
  ...ALL_CATALOG_ICON_NAMES,
  ...SEEDED_ICON_NAMES,
])

// Despite the name (its first use was Category.icon), this renders any lucide-react
// icon-name string — also used for Account.icon. Both were originally free-typed strings
// (pre-IconPicker Category data, and any account created before this field existed), so
// this can still see an emoji or a typo, not just a real icon name. Normalize common
// variations (case, spaces/underscores) before matching, and fall back to rendering the
// raw string rather than nothing for anything that still doesn't resolve, so old
// free-typed data never disappears.
function normalizeIconName(icon: string): string {
  return icon
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
}

// Seeded names lucide doesn't have, mapped to the glyph they meant.
const ICON_ALIASES: Record<string, string> = { bag: 'shopping-bag' }

function resolveIconName(icon: string): IconName | null {
  const normalized = normalizeIconName(icon)
  if (!KNOWN_ICON_NAMES.has(normalized)) return null
  return (ICON_ALIASES[normalized] ?? normalized) as IconName
}

export function CategoryIcon({
  icon,
  size = 14,
  className,
}: {
  icon: string
  size?: number
  className?: string
}) {
  const name = resolveIconName(icon)

  if (!name) {
    // No lucide match. An emoji is already a glyph — keep showing it. Anything else
    // (an empty value, or a stale/typo'd name like "home") would render as bare text,
    // so fall back to a neutral glyph. Tag is a static import, so this default survives
    // even the DynamicIcon chunk-load failure the KNOWN_ICON_NAMES set guards against.
    const isEmoji = /\p{Extended_Pictographic}/u.test(icon)
    return (
      <span data-testid="category-icon-fallback" aria-hidden="true" className={className}>
        {isEmoji ? icon : <Tag size={size} />}
      </span>
    )
  }

  return (
    <span data-testid="category-icon" className={className}>
      <DynamicIcon name={name} size={size} />
    </span>
  )
}
